import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, flatMap, map, retryWhen } from 'rxjs/operators';
import { UserSession, User } from 'SharedModels/app.models';
import { environment } from '../../../environments/environment';

const STAGING_URL = 'https://sherlock-staging.aerobotics.com/';
let localStorageStagingRedirects = {};

/** Function to extract the value of a cookie **/
export const getCookie = (name): string => {
  const value = '; ' + document.cookie;
  const parts = value.split('; ' + name + '=');
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return '';
};

/** Function to extract the relevant environments set in local storage if they exist and the app is not on production **/
const configureLocalStorageRedirects = () => {
  // Only allow redirects for production deployments. Local dev uses the proxy.
  if (typeof localStorage === 'undefined' || !environment.production) {
    return;
  }
  const localStorageRequestedRedirectsString = localStorage.getItem('aeroBackendEnvironments');
  try {
    const localStorageRequestedRedirects = JSON.parse(localStorageRequestedRedirectsString);
    Object.entries(localStorageRequestedRedirects).forEach(([api, environment]) => {
      // Currently we only support pointing to staging
      if (environment === 'staging') {
        localStorageStagingRedirects[api] = STAGING_URL;
      }
    });
  } catch (e) {
    console.error('Failed to load redirect config from localStorage');
  }
};


/** This variable determines whether cookies should be sent with every request or not **/
const SEND_COOKIES = true;


// This is copied verbatim from Angular’s http/http.ts.d file. Unfortunately,
// they defined this type in-line, so there’s no easy way to just import and
// use it directly.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type HttpGetOptions = {
  headers?: HttpHeaders | {
    [header: string]: string | string[];
  };
  observe?: 'body';
  params?: HttpParams | {
    [param: string]: string | string[];
  };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
};


const buildEndpoint = (url: string) => {
  const api = url.split('/')[0];
  return localStorageStagingRedirects[api] ? localStorageStagingRedirects[api] : `${environment.apiDomain}${url}`;
}


@Injectable()
export class AuthHttpService {
  useBackendEnvironmentsFromLocalStorage = false;
  // retryWhen:
  // Rule 1: If the returned observable emits any data successfully, the original source observable is resubscribed and retried.
  // Rule 2: If the returned observable emits an error then retryWhen gives up and emits the same error.
  // Rule 3: If the returned observable completes then retryWhen also completes.
  // retryWhen receives a stream of errors, one error each for every time the re-subscribed http observable throws an error.
  requestRetryPipeline =
  retryWhen(errors => errors.pipe(
    flatMap((error, i) => {
      // i represents the index of the error emitted by the observable
      const isFirst502Error = error.status === 502 && i === 0;

      if (isFirst502Error) {
        // returns a valid observable so the request can be resubscribed to again
        return of(error);
      }

      // throws the error which breaks out of the retry pipeline
      return throwError(error);
    }),
    delay(1000)
  )
  );

  constructor(private http: HttpClient) {
    configureLocalStorageRedirects();
  }

  // Authenticate the user
  login(email: string, password: string, userId: number): Observable<any> {
    if (userId !== null) {
      const url = 'gateway/users/' + userId + '/';
      const headers = this._getAuthorizationHeaders();
      return this.http.get(buildEndpoint(url), {headers: headers, withCredentials: SEND_COOKIES}).pipe(this.requestRetryPipeline)
        .pipe(
          map((user: User) => user)
        );
    } else {
      const url = 'gateway/auth/get_token/';
      return this.http.post(buildEndpoint(url),
        {username: email, password: password},
        {withCredentials: SEND_COOKIES})
        .pipe(this.requestRetryPipeline)
        .pipe(
          map((userSession: UserSession) => {
            if (typeof localStorage !== 'undefined') {
              // Update the local storage (FOR NOW KEEP THE OLD SESSION TOKEN THERE)
              localStorage.setItem('aeroUser', JSON.stringify({
                email: email,
                token: userSession.token,
                userId: userSession.user.id
              }));
            }
            return userSession.user;
          })
        );
    }
  }

  /** Function to authorize the api method **/
  private _getAuthorizationHeaders(): HttpHeaders {
    if (typeof localStorage !== 'undefined') {
      // Get the storage item
      const user = JSON.parse(localStorage.getItem('aeroUser'));
      let headers = new HttpHeaders().set('Authorization', 'null');
      // Make sure there is actually a user before adding the token an email to the headers
      if (user !== null) {
        headers = new HttpHeaders().set('Authorization', user.token);
      }
      return headers;
    }
    return new HttpHeaders().set('Authorization', 'Error');
  }

  confirmAccount(token: string, password: string, firstName: string, lastName: string, roleTypeId: number, phoneNumber: string
  ): Observable<any> {
    const postData = {
      token: token,
      password: password,
      first_name: firstName,
      last_name: lastName,
      role_type_id: roleTypeId,
      phone_number: phoneNumber
    };
    return this.http.post(
      buildEndpoint('gateway/users/confirm_account/'), postData, {withCredentials: SEND_COOKIES}
    ).pipe(
      map((responseData: any) => ({email: responseData.email}))
    ).pipe(this.requestRetryPipeline);
  }

  patch<T = object>(url, patchData): Observable<T> {
    const headers = this._getAuthorizationHeaders();
    return this.http.patch(
      buildEndpoint(url), patchData, {headers: headers, withCredentials: SEND_COOKIES}
    ).pipe(this.requestRetryPipeline) as Observable<T>;
  }

  post<T = object>(url: string, postData): Observable<T> {
    const headers = this._getAuthorizationHeaders();
    return this.http.post(
      buildEndpoint(url), postData, {headers: headers, withCredentials: SEND_COOKIES}
    ).pipe(this.requestRetryPipeline) as Observable<T>;
  }

  /** Temporary function to post to the .com domain **/
  postWithCredentials(url, postData): Observable<object> {
    const headers = this._getAuthorizationHeaders();
    return this.http.post(buildEndpoint(url), postData, {headers: headers, withCredentials: true})
      .pipe(this.requestRetryPipeline) as Observable<object>;
  }

  /** Function to download a file from a server **/
  downloadFile(url, postData, filename) {
    const headers = this._getAuthorizationHeaders();
    return new Observable<boolean>((observer) => {
      this.http.post(buildEndpoint(url), postData, {headers: headers, responseType: 'blob', withCredentials: SEND_COOKIES})
        .pipe(this.requestRetryPipeline)
        .subscribe((res: Blob) => {
          const returnUrl = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          document.body.appendChild(a);
          a.setAttribute('style', 'display: none');
          a.href = returnUrl;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(returnUrl);
          a.remove(); // remove the element
          observer.next(true);
          observer.complete();
        }, error => {
          console.log('download error:', JSON.stringify(error));
          observer.error(error);
        }, () => {
        });
    });
  }

  /** Function to download a file from a server **/
  downloadFileGetRequest(url, filename) {
    const headers = this._getAuthorizationHeaders();
    return new Observable<boolean>((observer) => {
      this.http.get(buildEndpoint(url), {headers: headers, responseType: 'blob', withCredentials: SEND_COOKIES})
        .pipe(this.requestRetryPipeline)
        .subscribe((res: Blob) => {
          const returnUrl = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          document.body.appendChild(a);
          a.setAttribute('style', 'display: none');
          a.href = returnUrl;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(returnUrl);
          a.remove(); // remove the element
          observer.next(true);
          observer.complete();
        }, error => {
          console.log('download error:', JSON.stringify(error));
          observer.error(error);
        }, () => {
        });
    });
  }

  get<T = object>(url: string, options?: HttpGetOptions): Observable<T> {
    const headers = this._getAuthorizationHeaders();
    return this.http.get(buildEndpoint(url), {headers: headers, withCredentials: SEND_COOKIES, ...options})
      .pipe(this.requestRetryPipeline)  as Observable<T>;
  }

  destroy<T = object>(url): Observable<T> {
    const headers = this._getAuthorizationHeaders();
    return this.http.delete(buildEndpoint(url), {headers: headers, withCredentials: SEND_COOKIES})
      .pipe(this.requestRetryPipeline)  as Observable<T>;
  }

  isAnyEndpointNonProdThroughLocalhost(): boolean {
    return !!Object.values(localStorageStagingRedirects).find(env => env === 'staging');
  }

}
